import React from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { CampaignName } from 'src/api/queries';
import { campaignCount } from 'src/features/Settings/utils/campaignFilter';

const PREVIEW_LIST_LIMIT = 20;

type CampaignPreviewProps = {
  isLoading: boolean;
  isError: boolean;
  campaigns: CampaignName[] | undefined;
  matchingCampaigns: CampaignName[] | null;
  filter: string | null;
};

export const CampaignPreview: React.FC<CampaignPreviewProps> = ({
  isLoading,
  isError,
  campaigns,
  matchingCampaigns,
  filter,
}) => {
  if (isLoading) {
    return (
      <Flex align="center" gap={2} mt={3}>
        <Spinner size="xs" color="content.muted" />
        <Text fontSize="sm" color="content.muted" fontFamily="marketingPage">
          Loading campaign list…
        </Text>
      </Flex>
    );
  }

  if (isError || !campaigns || !matchingCampaigns) {
    return (
      <Text
        mt={3}
        fontSize="sm"
        color="content.muted"
        fontFamily="marketingPage">
        Couldn&apos;t load the campaign list.
      </Text>
    );
  }

  if (!filter) {
    return (
      <Text
        mt={3}
        fontSize="sm"
        color="content.body"
        fontFamily="marketingPage">
        {campaignCount(campaigns.length)}{' '}
        {campaigns.length === 1 ? 'matches' : 'match'} the import scope.
      </Text>
    );
  }

  if (matchingCampaigns.length === 0) {
    return (
      <Text
        mt={3}
        fontSize="sm"
        color="background.red.500"
        fontFamily="marketingPage">
        No campaigns match this prefix.
      </Text>
    );
  }

  const visibleCampaigns = matchingCampaigns.slice(0, PREVIEW_LIST_LIMIT);
  const hiddenCount = matchingCampaigns.length - visibleCampaigns.length;

  return (
    <Box mt={3}>
      <Text
        fontSize="sm"
        fontWeight="600"
        color="content.title"
        fontFamily="marketingPage">
        Matches {matchingCampaigns.length} of {campaignCount(campaigns.length)}
      </Text>
      <Box
        mt={1}
        maxH="160px"
        overflowY="auto"
        borderWidth="1px"
        borderColor="surface.border"
        borderRadius="md"
        px={3}
        py={2}>
        {visibleCampaigns.map((campaign) => (
          <Text
            key={campaign.id}
            fontSize="sm"
            color="content.body"
            fontFamily="marketingPage"
            noOfLines={1}>
            {campaign.name}
          </Text>
        ))}
        {hiddenCount > 0 && (
          <Text
            fontSize="sm"
            color="content.muted"
            fontFamily="marketingPage"
            mt={1}>
            +{hiddenCount} more
          </Text>
        )}
      </Box>
    </Box>
  );
};
